import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Send, Loader2, Crown, Clock, Mail } from "lucide-react";
import { PortalShell } from "../components/layout/PortalShell";
import { apiGetOrg, apiInviteMember, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Workspace() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [sending, setSending] = useState(false);

  const load = () => apiGetOrg().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const org = data?.org;
  const isAdmin = org?.my_role === "admin";

  const invite = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter an email address."); return; }
    setSending(true);
    try {
      await apiInviteMember({ email, role });
      toast.success(`Invitation emailed to ${email}.`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the invitation.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PortalShell>
      <div data-testid="workspace-page">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy">{org?.name || "Team Workspace"}</h1>
            <p className="mt-1 text-muted-foreground">
              Everyone here shares one document library and report history.
            </p>
          </div>
          {org && (
            <span data-testid="workspace-my-role" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide ${isAdmin ? "bg-brand/10 text-brand" : "bg-secondary text-steel"}`}>
              {isAdmin && <Crown className="h-3.5 w-3.5" />} {org.my_role}
            </span>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          {/* Members */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-brand" />
                <h2 className="font-display text-lg font-bold text-navy">Members ({data?.members?.length ?? "—"})</h2>
              </div>
              <ul className="divide-y divide-border">
                {(data?.members || []).map((m) => (
                  <li key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-navy">{m.name || m.email}</div>
                      <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${m.role === "admin" ? "bg-brand/10 text-brand" : "bg-secondary text-steel"}`}>
                      {m.role}
                    </span>
                  </li>
                ))}
                {data && data.members.length === 0 && (
                  <li className="py-8 text-center text-sm text-muted-foreground">No members yet.</li>
                )}
              </ul>

              {data?.invites?.length > 0 && (
                <div className="mt-6 border-t border-border pt-5">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-navy">
                    <Clock className="h-4 w-4 text-aero" /> Pending invitations
                  </h3>
                  <ul className="space-y-2">
                    {data.invites.map((i) => (
                      <li key={i.email} data-testid={`invite-${i.email}`} className="flex items-center justify-between rounded-lg bg-secondary/60 px-4 py-2.5 text-sm">
                        <span className="flex items-center gap-2 text-navy"><Mail className="h-4 w-4 text-muted-foreground" /> {i.email}</span>
                        <span className="text-xs capitalize text-muted-foreground">{i.role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Invite */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <Send className="h-5 w-5 text-brand" />
                <h2 className="font-display text-lg font-bold text-navy">Invite a teammate</h2>
              </div>
              {isAdmin ? (
                <form onSubmit={invite} data-testid="invite-form" className="space-y-4">
                  <div>
                    <Label>Email address *</Label>
                    <Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" placeholder="colleague@company.com" />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger data-testid="invite-role" className="mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member — generate &amp; view reports</SelectItem>
                        <SelectItem value="admin">Admin — also invite &amp; manage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <button type="submit" disabled={sending} data-testid="invite-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-aero px-4 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invitation
                  </button>
                  <p className="text-xs text-muted-foreground">
                    They'll receive an email with a secure link. New teammates create a free account (or sign in) and join this workspace.
                  </p>
                </form>
              ) : (
                <p className="py-6 text-sm text-muted-foreground" data-testid="invite-admin-only">
                  Only workspace admins can invite teammates. Ask your admin to send invitations.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
